import React, { memo } from 'react';
import i18n from '../../../utilities/i18n';
import { FormikErrors, useFormikContext } from 'formik';
import {
  Button,
  HStack,
  Text,
  VStack,
  Table,
  IconButton,
  IconBasalcap,
  IconDiscard,
  ModalDesktop,
} from '@adc/parallax-component-library';
import { StudyCreateFormValues } from '../StudyCreate';
import { Subject } from '../../../models';
import CreateSubjectModal from '../../modal/CreateSubjectModal';
import PreventSubmitWrapper from '../../ui/forms/PreventSubmitWrapper';
import DynamicTable, { ColumnConfig } from '../../table/DynamicTable';
import MessageBanner from '../../messageBanner/MessageBanner';

const TabSubjects = () => {
  const { values, setFieldValue, errors } =
    useFormikContext<StudyCreateFormValues>();

  const tableColumns: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    { label: i18n.t('Entity.site'), dataKey: 'siteKey' },
    { label: '', dataKey: 'actions', isSortable: false },
  ];

  const cellRenderer = (dataKey: string, subject: Subject) => {
    const columnName = tableColumns.find((col) => col.dataKey === dataKey);
    let cell;

    if (columnName && columnName.dataKey === 'actions') {
      cell = (
        <Table.TextCell>
          <HStack gap="$2" padding="$2">
            <PreventSubmitWrapper>
              <CreateSubjectModal
                studyKey=""
                subject={subject}
                sites={values.sites}
                onSuccess={(updatedSubject: Subject) =>
                  setFieldValue(
                    'subjects',
                    values.subjects.map((s) =>
                      s.key === updatedSubject.key ? updatedSubject : s,
                    ),
                  ).then(
                    (errors: void | FormikErrors<StudyCreateFormValues>) => {
                      if (errors && Object.keys(errors).length > 0) {
                        return Promise.reject(errors);
                      }

                      return Promise.resolve();
                    },
                  )
                }
                trigger={
                  <IconButton
                    variant="tertiary"
                    onPress={() => {}}
                    aria-label={i18n.t('Common.edit')}
                  >
                    <IconBasalcap />
                  </IconButton>
                }
              />
              <IconButton
                variant="tertiary"
                onPress={() => {
                  ModalDesktop.show({
                    title: i18n.t(
                      'Page_StudyCreate.tab_subjects_delete_modal_title',
                    ),
                    details: i18n.t(
                      'Page_StudyCreate.tab_subjects_delete_modal_message',
                    ),
                    buttons: [
                      {
                        label: i18n.t('Common.cancel'),
                        variant: 'secondary',
                        onPress: () => {
                          ModalDesktop.hide();
                        },
                      },
                      {
                        label: i18n.t('Common.delete'),
                        onPress: () => {
                          setFieldValue(
                            'subjects',
                            values.subjects.filter(
                              (s) => s.key !== subject.key,
                            ),
                          ).then(() => ModalDesktop.hide());
                        },
                      },
                    ],
                  });
                }}
                aria-label={i18n.t('Common.delete')}
              >
                <IconDiscard />
              </IconButton>
            </PreventSubmitWrapper>
          </HStack>
        </Table.TextCell>
      );
    } else if (dataKey === 'siteKey') {
      const site = values.sites.find((s) => s.key === subject.siteKey);

      cell = <Table.TextCell>{site ? site.name : ''}</Table.TextCell>;
    } else {
      cell = (
        <Table.TextCell>
          {subject[dataKey as keyof Subject] || ''}
        </Table.TextCell>
      );
    }

    return cell;
  };

  return (
    <VStack
      height={'100%'}
      width={'100%'}
      gap="$2"
      backgroundColor="$neutral.0"
      padding="$2"
    >
      {values.sites.length === 0 ? (
        <VStack paddingVertical="$4">
          <MessageBanner
            type="info"
            bodyText={i18n.t('Page_StudyCreate.tab_subjects_no_sites')}
            iconSize="S"
          />
        </VStack>
      ) : null}
      <Text textAlign="left">
        {i18n.t('Page_StudyCreate.tab_subjects_instructions')}
      </Text>

      <HStack justifyContent="flex-end" gap="$2">
        <CreateSubjectModal
          studyKey=""
          trigger={
            <Button
              size="medium"
              label={i18n.t('Page_StudyCreate.tab_subjects_add')}
              onPress={() => {}}
              disabled={!values.sites.length}
            />
          }
          sites={values.sites}
          onSuccess={(subject: Subject) => {
            subject.key = `${values.subjectCount}`;

            return Promise.all([
              setFieldValue('subjectCount', values.subjectCount + 1),
              setFieldValue('subjects', [...values.subjects, subject]).then(
                (errors: void | FormikErrors<StudyCreateFormValues>) => {
                  if (errors && Object.keys(errors).length > 0) {
                    return Promise.reject(errors);
                  }
                },
              ),
            ])
              .then(() => {
                return Promise.resolve();
              })
              .catch((err) => {
                Promise.reject(err);
              });
          }}
        />
      </HStack>

      <DynamicTable
        showCard={false}
        columnData={tableColumns}
        isLoading={false}
        hasLoadingError={false}
        title=""
        description={i18n.t('Page_StudyCreate.tab_subjects_table')}
        rowData={values.subjects}
        cellRenderer={cellRenderer}
      />

      {(errors as any).subjects && (
        <Text
          color="$support.danger.default"
          type="$labelSmall.default"
          textAlign="left"
        >
          {(errors as any).subjects}
        </Text>
      )}
    </VStack>
  );
};

export default memo(TabSubjects);
